import type {
  GetChatBookingConversation,
  GetChatBookingIntegration,
  GetChatBookingStats,
  GetLimeAdminChatBooking,
  PostChatBookingConversationsList,
  PutChatBookingIntegration,
  PutLimeAdminChatBooking,
  PutLimeAdminChatBookingLocation,
} from "@/server-types";
import { queryClient } from "..";
import { useCustomMutation } from "../custom-mutation";
import { useCustomQuery } from "../custom-query";
import { get, post, put } from "../request-helpers";

/**
 * GET /chat-booking/integration
 */
export function useGetIntegration({ locationId }: { locationId: number }) {
  return useCustomQuery({
    queryKey: ["chatBooking", "integration", locationId],
    queryFn: () =>
      get<GetChatBookingIntegration>("/chat-booking/integration", {
        locationId,
      }),
    refetchOnWindowFocus: false,
    enabled: locationId > 0,
  });
}

/**
 * PUT /chat-booking/integration
 */
export function usePutIntegration(options?: { onSuccess?: () => void }) {
  return useCustomMutation({
    mutationFn: (body: PutChatBookingIntegration["body"]) =>
      put<PutChatBookingIntegration>("/chat-booking/integration", body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["chatBooking", "integration", variables.locationId],
      });
      options?.onSuccess?.();
    },
  });
}

/**
 * POST /chat-booking/conversations/list
 */
export function useConversationsList(
  body: PostChatBookingConversationsList["body"],
) {
  return useCustomQuery({
    queryKey: ["chatBooking", "conversations", body],
    queryFn: () =>
      post<PostChatBookingConversationsList>(
        "/chat-booking/conversations/list",
        body,
      ),
  });
}

/**
 * GET /chat-booking/conversations/:publicId
 */
export function useGetConversation({ publicId }: { publicId: string | null }) {
  return useCustomQuery({
    queryKey: ["chatBooking", "conversation", publicId],
    queryFn: () =>
      get<GetChatBookingConversation>(
        `/chat-booking/conversations/${publicId}`,
      ),
    enabled: publicId != null,
  });
}

/**
 * GET /chat-booking/stats
 */
export function useGetStats(
  query: GetChatBookingStats["query"],
  options?: { enabled?: boolean },
) {
  return useCustomQuery({
    queryKey: ["chatBooking", "stats", query],
    queryFn: () => get<GetChatBookingStats>("/chat-booking/stats", query),
    enabled: options?.enabled,
  });
}

/**
 * GET /lime-admin/client/[clientId]/chat-booking
 */
export function useAdminSettings(clientId: number) {
  return useCustomQuery({
    queryKey: ["chatBooking", "admin", clientId],
    queryFn: () =>
      get<GetLimeAdminChatBooking>(
        `/lime-admin/client/${clientId}/chat-booking`,
      ),
    refetchOnWindowFocus: false,
  });
}

/**
 * PUT /lime-admin/client/[clientId]/chat-booking
 */
export function usePutAdminSettings(
  clientId: number,
  options?: { onSuccess?: () => void },
) {
  return useCustomMutation({
    mutationFn: (body: PutLimeAdminChatBooking["body"]) =>
      put<PutLimeAdminChatBooking>(
        `/lime-admin/client/${clientId}/chat-booking`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["chatBooking", "admin", clientId],
      });
      options?.onSuccess?.();
    },
  });
}

/**
 * PUT /lime-admin/client/[clientId]/chat-booking/location/[locationId]
 */
export function usePutAdminLocation(
  clientId: number,
  options?: { onSuccess?: () => void },
) {
  return useCustomMutation({
    mutationFn: ({
      locationId,
      body,
    }: {
      locationId: number;
      body: PutLimeAdminChatBookingLocation["body"];
    }) =>
      put<PutLimeAdminChatBookingLocation>(
        `/lime-admin/client/${clientId}/chat-booking/location/${locationId}`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["chatBooking", "admin", clientId],
      });
      options?.onSuccess?.();
    },
  });
}
